import axiosInstance from '../axiosInstance';
import { SREDFilters, SREDReportResponse, SREDConsultant } from './types';

const getApiUrl = (endpoint: string): string => {
  if (process.env.NODE_ENV === 'production') {
    return `/api3.2/sred${endpoint}`;
  }
  if (window.location.port === '8000') {
    return `/api3.2/sred${endpoint}`;
  }
  return `http://localhost:10000/api3.2/sred${endpoint}`;
};

const getForwardedHost = (apiUrl: string): string => {
  try {
    const parsedUrl = apiUrl.startsWith('http')
      ? new URL(apiUrl)
      : new URL(apiUrl, window.location.origin);
    return parsedUrl.host;
  } catch {
    return window.location.host;
  }
};

function unwrapResponse<T>(response: { data: { success: boolean; data: T } | T }): T {
  const data = response.data;
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    return data.data;
  }
  return data as T;
}

export async function getSREDReport(filters: SREDFilters): Promise<SREDReportResponse> {
  const API_URL = getApiUrl('/report');

  try {
    const params: Record<string, string> = {
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      sredYear: filters.sredYear,
    };
    if (filters.consultantIds && filters.consultantIds.length > 0) {
      params.consultantIds = filters.consultantIds.join(',');
    }
    if (filters.projects && filters.projects.length > 0) {
      params.projects = filters.projects.join(',');
    }

    const response = await axiosInstance.get(API_URL, {
      params,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<SREDReportResponse>(response);
  } catch (error) {
    console.error('[SRED] Failed to fetch report:', error);
    throw error;
  }
}

export async function getSREDProjects(sredYear: string): Promise<string[]> {
  const API_URL = getApiUrl('/projects');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: { sredYear },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<string[]>(response);
  } catch (error) {
    console.error('[SRED] Failed to fetch projects:', error);
    throw error;
  }
}

export async function getSREDConsultants(
  startDate: string,
  endDate: string,
  sredYear: string,
): Promise<SREDConsultant[]> {
  const API_URL = getApiUrl('/consultants');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: { startDate, endDate, sredYear },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<SREDConsultant[]>(response);
  } catch (error) {
    console.error('[SRED] Failed to fetch consultants:', error);
    throw error;
  }
}

export async function downloadSREDExcel(filters: SREDFilters): Promise<void> {
  const API_URL = getApiUrl('/export');

  try {
    const params: Record<string, string> = {
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      sredYear: filters.sredYear,
    };
    if (filters.consultantIds && filters.consultantIds.length > 0) {
      params.consultantIds = filters.consultantIds.join(',');
    }
    if (filters.projects && filters.projects.length > 0) {
      params.projects = filters.projects.join(',');
    }

    const response = await axiosInstance.get(API_URL, {
      params,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `sred_${filters.sredYear}_hours_report.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('[SRED] Failed to download Excel report:', error);
    throw error;
  }
}
